// useProjectParams — typed wrapper around React Router's useParams.
//
// Per ADR-016, project context lives in the URL only. The raw useParams
// returns `Partial<Record<string, string>>` — `projectId` could be undefined,
// and every consumer would have to non-null-assert or branch. This wrapper
// asserts once + returns `string` so consumers can trust the value.
//
// ESLint `no-restricted-imports` bans raw useParams outside this file (see
// `eslint.config.js`) — guarantees the wrapper is the single entry point.
//
// Two exports:
//   - useProjectParams() → string. For routes mounted inside /p/:projectId/*
//     where projectId is structurally guaranteed by the route shape. Throws
//     if absent (programmer error — wrong route mount).
//   - useOptionalProjectParams() → string | null. For cross-cutting
//     components (ThemeProvider, Navbar) that render across project and
//     project-less routes both.

import { useParams } from 'react-router-dom';

/**
 * Returns the current projectId from the URL. Throws if the consumer is
 * mounted outside a /p/:projectId/* route — programmer error.
 */
export function useProjectParams(): string {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) {
    throw new Error(
      'useProjectParams called outside a /p/:projectId/* route. Use useOptionalProjectParams instead.',
    );
  }
  return projectId;
}

/**
 * Returns the current projectId from the URL, or null if absent. Use in
 * cross-cutting components that mount on both project and project-less
 * routes (ThemeProvider, Navbar, etc.).
 */
export function useOptionalProjectParams(): string | null {
  const { projectId } = useParams<{ projectId: string }>();
  return projectId ?? null;
}

/**
 * Returns a required entity-id path param (`:userId`, `:roleId`, …) as a non-null
 * string, asserting presence the way useProjectParams does for projectId. For
 * entity detail routes mounted under a `:xId` segment. Lives here because this is
 * the ONE sanctioned useParams wrapper (the no-restricted-imports gate bans raw
 * useParams everywhere else).
 */
export function useRequiredParam(name: string): string {
  const value = useParams()[name];
  if (!value) {
    throw new Error(`useRequiredParam("${name}") called outside a route with a :${name} segment.`);
  }
  return value;
}
